import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  HStack,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import axios from "../../axiosConfig";
import { Link } from "react-router-dom";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";
import PaginatedListToolbar from "../common/PaginatedListToolbar";
import PaginationControls from "../common/PaginationControls";
import usePaginatedList from "../common/usePaginatedList";
import { getTrialStatusDisplay } from "../../utils/trialAccess";

const NAME_MAX_CHARS = 30;
const CONTACT_MAX_CHARS = 20;
const EMAIL_MAX_CHARS = 30;
const PHONE_MAX_CHARS = 15;

const OrganizationList = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: 'md' });

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get("/organizations");
        setOrganizations(response.data ?? []);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrganizations();
  }, []);

  const organizationSearchFields = (organization) => [
    organization?.name,
    organization?.contact,
    organization?.city,
    organization?.country,
    organization?.email,
    organization?.phone,
    organization?.user?.label,
    organization?.network?.label,
  ];

  const organizationSortOptions = useMemo(
    () => [
      { key: "name", label: "Name", getSortValue: (organization) => organization?.name },
      { key: "contact", label: "Contact", getSortValue: (organization) => organization?.contact },
      {
        key: "location",
        label: "Location",
        getSortValue: (organization) =>
          [organization?.city, organization?.country].filter(Boolean).join(", "),
      },
    ],
    []
  );

  const {
    paginatedItems: paginatedOrganizations,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    searchText,
    sortKey,
    sortDir,
    setCurrentPage,
    setPageSize,
    setSearchText,
    setSortDir,
    setSortKey,
    toggleSort,
  } = usePaginatedList({
    items: organizations,
    sortOptions: organizationSortOptions,
    initialSortKey: "name",
    initialSortDir: "asc",
    initialPageSize: 25,
    searchMatcher: (organization, normalizedSearch) =>
      organizationSearchFields(organization).some((value) =>
        value?.toString().toLowerCase().includes(normalizedSearch)
      ),
  });

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  /* ---------------- UI bits ---------------- */

  const UserBadge = ({ hasUser }) => (
    <Badge
      colorScheme={hasUser ? "blue" : "gray"}
      variant="subtle"
      borderRadius="md"
    >
      {hasUser ? "User assigned" : "No user"}
    </Badge>
  );

  const TrialStatusBadge = ({ organization }) => {
    const trialAccess = getTrialStatusDisplay(organization);

    const label =
      trialAccess.trialStatus === "pending"
        ? t("trial_access.status.pending")
        : trialAccess.trialStatus === "active"
          ? t("trial_access.status.active")
          : trialAccess.trialStatus === "expired"
            ? t("trial_access.status.expired")
            : t("trial_access.status.none");

    const details =
      trialAccess.trialStatus === "active" && trialAccess.daysRemaining != null
        ? t("trial_access.organization.list_active_with_days", { count: trialAccess.daysRemaining })
        : null;

    return (
      <VStack align="start" spacing={1}>
        <Badge colorScheme={trialAccess.colorScheme} variant="subtle" borderRadius="md">
          {label}
        </Badge>
        {details ? (
          <Text fontSize="xs" color={subTextColor}>
            {details}
          </Text>
        ) : null}
      </VStack>
    );
  };

  const renderOrgCard = (org) => (
    <Box
      key={org.id}
      p={4}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      shadow="sm"
      bg={cardBg}
      _hover={{ shadow: "md" }}
    >
      <VStack align="start" spacing={3} w="100%">
        <HStack justify="space-between" w="100%" align="start">
          <Box minW={0}>
            <TruncatedText
              value={org.name}
              maxChars={NAME_MAX_CHARS}
              fontWeight="bold"
            />
            <Text fontSize="sm" color={subTextColor}>
              {org.contact ? `Contact: ${org.contact}` : "Contact: N/A"}
            </Text>
          </Box>

          <UserBadge hasUser={org.has_user} />
        </HStack>

        <TruncatedText
          value={org.email || "N/A"}
          maxChars={EMAIL_MAX_CHARS}
        />

        <TruncatedText
          value={org.phone || "N/A"}
          maxChars={PHONE_MAX_CHARS}
          tooltip={false}
        />

        <TrialStatusBadge organization={org} />

        <Button
          as={Link}
          to={`/app/organizations/${org.id}`}
          size="sm"
          variant="outline"
          colorScheme="cyan"
          alignSelf="flex-end"
        >
          View
        </Button>
      </VStack>
    </Box>
  );

  const renderOrgTable = () => (
    <Box
      w="100%"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      bg={cardBg}
      shadow="sm"
      overflow="hidden"
    >
      <TableContainer overflowX="auto">
        <Table
          variant="unstyled"
          sx={{
            tableLayout: "fixed",
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "26%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>

          <Thead>
            <Tr bg={cardBg} position="sticky" top={0} zIndex={1}>
              <Th
                cursor="pointer"
                onClick={() => toggleSort("name")}
                fontSize="xs"
                textTransform="uppercase"
              >
                Name <SortIndicator active={sortKey === "name"} />
              </Th>

              <Th
                cursor="pointer"
                onClick={() => toggleSort("contact")}
                fontSize="xs"
                textTransform="uppercase"
              >
                Contact <SortIndicator active={sortKey === "contact"} />
              </Th>

              <Th fontSize="xs" textTransform="uppercase">Email</Th>
              <Th fontSize="xs" textTransform="uppercase">Phone</Th>
              <Th fontSize="xs" textTransform="uppercase">{t("trial_access.organization.list_column")}</Th>
              <Th fontSize="xs" textTransform="uppercase">User</Th>
            </Tr>
          </Thead>

          <Tbody>
            {paginatedOrganizations.map((org) => (
              <Tr key={org.id}>
                <Td>
                  <TruncatedText
                    value={org.name}
                    maxChars={NAME_MAX_CHARS}
                    fontWeight="bold"
                    as={Link}
                    to={`/app/organizations/${org.id}`}
                    color="teal.500"
                  />
                </Td>

                <Td>
                  <TruncatedText
                    value={org.contact || "N/A"}
                    maxChars={CONTACT_MAX_CHARS}
                  />
                </Td>

                <Td>
                  <TruncatedText
                    value={org.email || "N/A"}
                    maxChars={EMAIL_MAX_CHARS}
                  />
                </Td>

                <Td>
                  <TruncatedText
                    value={org.phone || "N/A"}
                    maxChars={PHONE_MAX_CHARS}
                    tooltip={false}
                  />
                </Td>

                <Td>
                  <TrialStatusBadge organization={org} />
                </Td>

                <Td>
                  <UserBadge hasUser={org.has_user} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  /* ---------------- header actions ---------------- */

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      <Button
        as={Link}
        to="/app/organizations/create"
        colorScheme="green"
        variant="outline"
        size="sm"
      >
        Add Organization
      </Button>
    </HStack>
  );

  return (
    <ListPageLayout title="Schools" actions={actions}>
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} mb={4}>
        <CardBody>
          <PaginatedListToolbar
            searchText={searchText}
            onSearchTextChange={setSearchText}
            searchPlaceholder="Search by name, contact, or location"
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
            sortKey={sortKey}
            sortDir={sortDir}
            sortOptions={organizationSortOptions}
            onSortKeyChange={setSortKey}
            onSortDirChange={setSortDir}
          />
        </CardBody>
      </Card>

      {loading ? (
        <Flex justify="center" mt={10}>
          <Spinner size="xl" color="orange.400" />
        </Flex>
      ) : totalCount > 0 ? (
        isMobile ? (
          <VStack spacing={4} mt={2} align="stretch">
            {paginatedOrganizations.map(renderOrgCard)}
          </VStack>
        ) : (
          <Box mt={2}>{renderOrgTable()}</Box>
        )
      ) : (
        <Text mt={10} textAlign="center">
          No organizations found.
        </Text>
      )}
      {!loading && totalCount > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      ) : null}
    </ListPageLayout>
  );
};

export default OrganizationList;
